"use client";

import { useEffect, useState } from "react";

import upperFirst from "lodash/upperFirst";
import { FormattedMessage, useIntl } from "react-intl";

import { Badge } from "@/components/atoms/Badge";
import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import MoneyIcon from "@/icons/__generated__/Money/Money24";
import { useHash } from "@/lib/hooks/use-hash";
import { getFoodAndDrinkMessage } from "~/components/listings/full-service-venues/utils/fsv.utils";
import { useListingData } from "~/context/ListingPageDataContext";
import { isMinimumSpendDepositListing } from "~/utils/pay-at-venue.utils";

import HowMinimumSpendWorks from "./HowMinimumSpendWorks";

export const FsvCreditAccordion = () => {
  const intl = useIntl();
  const { data: listingData } = useListingData();
  const { hashMap, updateHash } = useHash();
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  const isDepositListing = isMinimumSpendDepositListing(
    listingData?.payAtVenue,
  );
  const categoryPhrase = getFoodAndDrinkMessage(
    listingData?.fullService?.drink_options,
    listingData?.fullService?.food_packages?.[0],
    intl,
  );
  const categoryTitle = upperFirst(categoryPhrase);
  const useDepositMinimumCopy = isDepositListing && !!categoryTitle;

  useEffect(() => {
    if (hashMap.has("how-credit-works")) {
      // open the accordion when the hash is present
      setAccordionValue(["how-credit-works"]);
    }
  }, [hashMap]);

  return (
    <Accordion
      type="multiple"
      value={accordionValue}
      onValueChange={(value) => {
        if (!value.length) {
          // if the accordion is closed, clear the hash
          updateHash({});
        }
        setAccordionValue(value);
      }}
    >
      <AccordionItem value="how-credit-works">
        <AccordionTrigger className="tw-py-24 tw-text-left">
          <div className="tw-flex tw-gap-12">
            <MoneyIcon className="tw-basis-24" />

            {/* tw-pr-44 is to account for the NEW! badge (width plus 4px gap) */}
            <div className="tw-flex-1 tw-relative tw-pr-44">
              <Text typeface="headline-1-m">
                {useDepositMinimumCopy ? (
                  <FormattedMessage
                    defaultMessage="{category} minimum"
                    values={{ category: categoryTitle }}
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Minimum spend at venue"
                    id="fsv-minimum-spend-at-venue-accordion-title"
                  />
                )}
              </Text>
              <div
                className="tw-absolute tw-top-0 tw-bottom-0 tw-right-0 tw-flex tw-items-center"
                style={{ textDecoration: "none !important" }}
              >
                <Badge appearance="purple" size="xs">
                  NEW!
                </Badge>
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="tw-ml-36 tw-pb-24">
          <HowMinimumSpendWorks
            useDepositCopy={useDepositMinimumCopy}
            categoryPhrase={categoryPhrase}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
